import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useTheme,
} from '@mui/material';
import ClientSearch from '../ClientSearch';
import ObservacaoDoCliente from '../ObservacaoDoCliente';

/**
 * Escrever a observação de um cliente A PARTIR DO MURAL.
 *
 * ## O pedido
 *
 * Ponto 35 da Gisele, terceira parte, esclarecida pelo dono do produto em
 * 23/10/2026: "falta conseguir criar uma observação diretamente de um cliente
 * pelo mural, em vez de ser somente ao selecionar o cliente no ticket ou no
 * FAB".
 *
 * As outras duas partes já existiam: a observação aparece fixa no cartão do
 * cliente dentro do chamado (fora do "expandir", de propósito), e o serviço
 * `ObservacaoDoCliente` da api espelha cada uma como uma linha do Mural. O que
 * faltava era o caminho de ida -- do Mural para o cliente.
 *
 * ## Por que um diálogo próprio, e não mais um tipo no "Novo aviso"
 *
 * O formulário de publicar tem público-alvo, departamentos, agendamento,
 * prazo e anexos. Nenhum deles vale para uma observação: ela é da empresa,
 * aparece para todos, não agenda, não expira e não tem anexo. Um terceiro tipo
 * ali seria um formulário em que quase todo campo some ao escolhê-lo.
 *
 * ## Nada de novo é escrito aqui
 *
 * A busca é o `ClientSearch` de sempre e a escrita é o `ObservacaoDoCliente`
 * de sempre -- o mesmo componente do chamado, com a mesma permissão, o mesmo
 * `PUT /companies/{id}/observacao` e o mesmo espelho no Mural. Reescrever a
 * caixa de texto aqui criaria uma segunda forma de salvar a mesma coisa, que
 * é exatamente o tipo de divergência que custou caro em 23/10.
 *
 * O servidor já estava pronto para isto: o comentário de
 * `CompanyController::updateObservacao` diz que o espelho é feito no serviço
 * "para continuar correto se amanhã a observação for salva de outro lugar".
 * Hoje é esse outro lugar.
 */
const NovaObservacaoPeloMural = ({ aberto, onFechar, onSalvou }) => {
    const theme = useTheme();
    const [cliente, setCliente] = useState(null);

    const fechar = () => {
        // O cliente escolhido NÃO sobrevive ao fechamento: reabrir caindo no
        // cliente anterior é como se escreve observação na empresa errada.
        setCliente(null);
        onFechar();
    };

    return (
        <Dialog open={Boolean(aberto)} onClose={fechar} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ color: `${theme.palette.text.primary} !important` }}>
                Observação de cliente
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                    Escolha o cliente. A observação aparece no atendimento, no chamado dele e aqui no
                    Mural.
                </Typography>

                <ClientSearch
                    onClientSelect={setCliente}
                    selectedClient={cliente}
                    placeholder="Digite código (ex: 1405), nome ou CNPJ..."
                />

                {/*
                  * Só depois de escolher. O painel sem empresa não renderiza
                  * nada (`if (!empresa?.id) return null`), e uma área vazia sob
                  * a busca pareceria um campo que não carregou.
                  */}
                {cliente?.company?.id && (
                    <div style={{ marginTop: 16 }} data-testid="painel-da-observacao">
                        <ObservacaoDoCliente
                            empresa={cliente.company}
                            onMudou={(empresaAtualizada) => {
                                // O cliente em mãos acompanha o que foi salvo, para
                                // o painel continuar coerente se o operador editar
                                // duas vezes sem fechar.
                                setCliente((atual) => (atual
                                    ? { ...atual, company: { ...atual.company, ...empresaAtualizada } }
                                    : atual));
                                if (onSalvou) onSalvou();
                            }}
                        />
                    </div>
                )}

                {cliente && !cliente?.company?.id && (
                    <Typography variant="body2" sx={{ color: theme.palette.warning.main, mt: 2 }}>
                        Este contato não está vinculado a uma empresa, e a observação é da empresa.
                        Associe o cliente primeiro, pelo chamado.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={fechar} sx={{ color: `${theme.palette.text.secondary} !important` }}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NovaObservacaoPeloMural;
